import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { analyticsAPI, capsulesAPI, capsuleConfigAPI, ticketsAPI, formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import GanttToolbar, { VENTANAS } from '@/components/gantt/GanttToolbar';
import GanttChart from '@/components/gantt/GanttChart';
import GanttGuia from '@/components/gantt/GanttGuia';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import GanttDiff from '@/components/gantt/GanttDiff';
import { PX_POR_DIA_DEFECTO, PX_POR_DIA_MAX, PX_POR_DIA_MIN } from '@/components/gantt/ganttEje';
import { buildTicketDeepLink } from '@/components/gantt/ganttUtils';

const PRIORITIES = ['', 'low', 'medium', 'high', 'critical'];
// La guia se abre sola la primera vez que alguien entra al Gantt, y despues no molesta mas.
// Es lo contrario del modal de `HelpButton`: no tapa el grafico, no hay que cerrarla para
// trabajar, y el boton de la barra la trae de vuelta cuando haga falta.
const GUIA_VISTA = 'omnisys.gantt.guia.vista';
// Desde cuando arranca el eje. Se recuerda por navegador, como el zoom: quien acota el rango
// para trabajar no quiere volver a acotarlo en cada entrada.
const VENTANA_ELEGIDA = 'omnisys.gantt.ventana';
// Los estados salen del catálogo de la cápsula. Estaban hardcodeados —y encima incompletos:
// faltaban tres de los ocho de entonces—, así que un taller con `en_taller` no habría podido
// filtrar por su propio estado.

export default function ExecutiveGanttPage() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [capsules, setCapsules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [ticketStatuses, setTicketStatuses] = useState([]);
  const [filters, setFilters] = useState({
    capsule_id: searchParams.get('capsule') || searchParams.get('capsule_id') || '',
    department: searchParams.get('department') || '',
    priority: searchParams.get('priority') || '',
    priority_in: searchParams.get('priority_in') || '',
    status: searchParams.get('status') || '',
    status_scope: searchParams.get('status_scope') || '',
    person_id: searchParams.get('person_id') || '',
  });
  // Zoom continuo, no tres escalas. Ver `components/gantt/ganttEje.js`.
  const [pxPorDia, setPxPorDia] = useState(PX_POR_DIA_DEFECTO);
  const [verCarga, setVerCarga] = useState(true);
  const [carga, setCarga] = useState(null);
  // La simulacion pendiente. Existe entre soltar una barra y confirmar: mientras esta, el
  // grafico dibuja el plan **propuesto** y no se puede arrastrar otra cosa — dos cambios sin
  // confirmar encima no se pueden explicar en un solo diff.
  const [sim, setSim] = useState(null);
  const [simTitulo, setSimTitulo] = useState('');
  const [simCambios, setSimCambios] = useState([]);
  const [simTicket, setSimTicket] = useState(null);
  const [simCapsula, setSimCapsula] = useState(null);
  const [simulando, setSimulando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [simError, setSimError] = useState('');
  const [guiaAbierta, setGuiaAbierta] = useState(() => {
    // En modo privado `localStorage` tira: la guia se vuelve a abrir, que es el peor caso
    // aceptable. Romper la pantalla por una preferencia no lo es.
    try { return localStorage.getItem(GUIA_VISTA) !== '1'; } catch { return true; }
  });
  const [showAssignees, setShowAssignees] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    capsulesAPI.list(tenantId).then((r) => setCapsules(r.data || [])).catch(() => {});
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !filters.capsule_id) { setDepartments([]); setTicketStatuses([]); return; }
    capsuleConfigAPI.get(tenantId, filters.capsule_id)
      .then((r) => {
        setDepartments(r.data?.requesting_departments || []);
        setTicketStatuses(r.data?.ticket_statuses || []);
      })
      .catch(() => { setDepartments([]); setTicketStatuses([]); });
  }, [tenantId, filters.capsule_id]);

  const ganttApi = useRef(null);
  const [ventana, setVentana] = useState(() => {
    try { return localStorage.getItem(VENTANA_ELEGIDA) || 'todo'; } catch { return 'todo'; }
  });
  const [fueraDeRango, setFueraDeRango] = useState(0);
  const [vinculoAQuitar, setVinculoAQuitar] = useState(null);

  const cerrarGuia = useCallback(() => {
    setGuiaAbierta(false);
    try { localStorage.setItem(GUIA_VISTA, '1'); } catch { /* preferencia perdida, nada mas */ }
  }, []);

  const cleanParams = useCallback(() => {
    const p = {};
    // **`from_date` existia en el endpoint desde siempre y el front nunca lo mandaba.** El rango
    // lo decidia el backend como `min(todas las fechas)`, y como un caso sin plan ancla en su
    // fecha de creacion, el eje arrancaba en 2016 — habia que scrollear diez años hasta esta
    // semana. El default sigue siendo «todo» (ver `VENTANAS`), asi que esto solo viaja cuando
    // alguien acota el rango a mano.
    const dias = VENTANAS.find((v) => v.id === ventana)?.dias;
    if (dias) {
      const desde = new Date();
      desde.setDate(desde.getDate() - dias);
      p.from_date = desde.toISOString().slice(0, 10);
    }
    if (filters.capsule_id) p.capsule_id = filters.capsule_id;
    if (filters.department) p.department = filters.department;
    if (filters.priority_in) p.priority_in = filters.priority_in;
    else if (filters.priority) p.priority = filters.priority;
    if (filters.status_scope) p.status_scope = filters.status_scope;
    else if (filters.status) p.status = filters.status;
    if (filters.person_id) p.person_id = filters.person_id;
    return p;
  }, [filters, ventana]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsAPI.pmoGantt(cleanParams());
      setData(res.data);
      // La carga se pide **con el rango que devolvio el Gantt**, no con uno propio: las dos
      // vistas comparten el eje, y pedir rangos distintos es la forma mas facil de que la
      // franja de abajo hable de otros dias que las barras de arriba.
      const meta = res.data?.meta;
      if (meta?.range_start && meta?.range_end) {
        const c = await analyticsAPI
          .pmoCapacidad({
            desde: meta.range_start,
            hasta: meta.range_end,
            ...(filters.capsule_id ? { capsule_id: filters.capsule_id } : {}),
          })
          .catch(() => null);
        setCarga(c?.data || null);
      } else {
        setCarga(null);
      }
    } catch {
      toast.error('No se pudo cargar el Gantt');
      setData(null);
      setCarga(null);
    } finally {
      setLoading(false);
    }
  }, [cleanParams, filters.capsule_id]);

  useEffect(() => { if (tenantId) load(); }, [tenantId, load, filters]);

  const limpiarSim = useCallback(() => {
    setSim(null); setSimCambios([]); setSimTicket(null); setSimCapsula(null);
    setSimError(''); setSimTitulo('');
  }, []);

  /**
   * La fila de una tarea. De ahi salen **su** caso y **su** capsula.
   *
   * Antes se usaba `filters.capsule_id` para armar la URL, y con el filtro en "Todas" ese
   * valor es vacio: la peticion salia a `/capsules//tickets/...` y volvia 404. Y aunque el
   * filtro tuviera valor seguiria estando mal — el tablero sin filtrar muestra casos de varias
   * capsulas, asi que una capsula sola no puede hablar por todas. La capsula es **del caso**,
   * no de lo que uno este mirando.
   */
  const filaDeTarea = useCallback(
    (taskId) => (data?.rows || []).find((r) => r.task_id === taskId) || null,
    [data],
  );

  const simular = useCallback(async (cambios, ticketId, capsuleId, titulo) => {
    if (!ticketId || !capsuleId) {
      setSimError('No se pudo ubicar el caso de esa tarea. Recargá el tablero.');
      return;
    }
    setSimulando(true); setSimError(''); setSimTitulo(titulo || '');
    try {
      const res = await ticketsAPI.simularReplanificacion(
        tenantId, capsuleId, ticketId, { cambios },
      );
      setSim(res.data); setSimCambios(cambios); setSimTicket(ticketId); setSimCapsula(capsuleId);
    } catch (e) {
      // El 403 de planificar dice que hacer; se muestra tal cual y no se traduce a "error".
      setSimError(formatApiError(e)); setSim(null);
    } finally {
      setSimulando(false);
    }
  }, [tenantId]);

  const confirmarSim = useCallback(async () => {
    if (!simTicket || !simCapsula) return;
    setAplicando(true);
    try {
      await ticketsAPI.aplicarReplanificacion(
        tenantId, simCapsula, simTicket, { cambios: simCambios },
      );
      toast.success('Plan actualizado');
      limpiarSim();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAplicando(false);
    }
  }, [tenantId, simCapsula, simTicket, simCambios, limpiarSim, load]);

  const nuevoVinculo = useCallback(async (desdeTaskId, haciaTaskId) => {
    const desde = filaDeTarea(desdeTaskId);
    const hacia = filaDeTarea(haciaTaskId);
    if (!hacia?.ticket_id || !hacia?.capsule_id) {
      toast.error('No se pudo ubicar el caso de esa tarea. Recargá el tablero.');
      return;
    }
    // Un vinculo vive dentro de una capsula: el backend busca las dos puntas ahi. Cruzar
    // capsulas devolveria "no existe la tarea", que manda a buscar el problema donde no esta.
    if (desde?.capsule_id && desde.capsule_id !== hacia.capsule_id) {
      toast.error('No se pueden vincular tareas de dos cápsulas distintas.');
      return;
    }
    try {
      await ticketsAPI.createScheduleLink(tenantId, hacia.capsule_id, {
        from_kind: 'task', from_id: desdeTaskId,
        to_kind: 'task', to_id: haciaTaskId,
        link_type: 'finish_to_start', lag_dias: 0,
      });
      toast.success('Dependencia creada');
      await load();
      // El vinculo ya quedo, pero **las fechas no se movieron solas**: lo que la dependencia
      // implica se simula y se confirma aparte, igual que cualquier otra replanificacion.
      await simular([], hacia.ticket_id, hacia.capsule_id, 'Lo que implica la dependencia nueva');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [tenantId, filaDeTarea, load, simular]);

  /**
   * Quitar una dependencia **desde el Gantt**.
   *
   * Hasta el 2026-10-08 el gesto estaba a la mitad: se creaba con dos toques y no se podia
   * borrar —el dibujo de las flechas era `pointer-events: none`— asi que para deshacer habia que
   * ir al tablero del caso. Y el `link_id` ni viajaba en la respuesta.
   *
   * **Se confirma antes, y con el diálogo del producto.** No es destructivo de datos —el vínculo
   * se vuelve a crear con dos toques— pero sacarlo cambia el plan de la sucesora, y un clic en
   * una línea de 1 px es demasiado fácil de hacer sin querer. El confirm nativo del navegador
   * está prohibido para esto en el `CLAUDE.md` y hay un test que lo escanea, así que va por
   * `ConfirmActionDialog`; y en `destructivo={false}`, porque el rojo tiene que seguir
   * significando algo.
   */
  const quitarVinculo = useCallback((flecha) => {
    if (!flecha?.linkId) return;
    const capsula = flecha.capsuleId || filters.capsule_id;
    if (!capsula) {
      toast.error('No se pudo ubicar la cápsula de esa dependencia. Recargá el tablero.');
      return;
    }
    setVinculoAQuitar({ linkId: flecha.linkId, capsula, titulo: flecha.titulo || '' });
  }, [filters.capsule_id]);

  const confirmarQuitarVinculo = useCallback(async () => {
    const v = vinculoAQuitar;
    if (!v) return;
    setVinculoAQuitar(null);
    try {
      await ticketsAPI.deleteScheduleLink(tenantId, v.capsula, v.linkId);
      toast.success('Dependencia quitada');
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [tenantId, vinculoAQuitar, load]);

  /** Donde quedaria cada tarea si se confirmara. Lo dibuja el grafico sobre el plan actual. */
  const propuesta = useMemo(() => {
    if (!sim?.movidas?.length) return null;
    const m = {};
    sim.movidas.forEach((x) => { m[x.task_id] = x.despues; });
    return m;
  }, [sim]);

  /**
   * Quien tiene trabajo en el tablero, para el filtro por persona.
   *
   * Sale de `meta.personas`, que el backend arma **antes** de aplicar el filtro. Si se armara
   * con las filas visibles, elegir a alguien dejaria la lista con esa sola persona y no habria
   * como cambiar ni volver. Y si el filtro viene por URL apuntando a alguien que no esta en
   * este tablero, se agrega igual: es peor perderlo en silencio que mostrarlo raro.
   */
  const personasDelTablero = useMemo(() => {
    const lista = data?.meta?.personas || [];
    const actual = (filters.person_id || '').trim().toLowerCase();
    if (actual && !lista.some((p) => p.id === actual)) {
      return [...lista, { id: actual, name: `${actual} — sin tareas en este tablero`, tareas: 0 }];
    }
    return lista;
  }, [data, filters.person_id]);

  const handleRowClick = (row) => {
    const url = row.open_url || buildTicketDeepLink(row);
    if (url) navigate(url);
  };

  const filterQuery = () => {
    const p = new URLSearchParams();
    if (filters.capsule_id) p.set('capsule', filters.capsule_id);
    if (filters.department) p.set('department', filters.department);
    if (filters.priority_in) p.set('priority_in', filters.priority_in);
    else if (filters.priority) p.set('priority', filters.priority);
    if (filters.status_scope) p.set('status_scope', filters.status_scope);
    else if (filters.status) p.set('status', filters.status);
    if (filters.person_id) p.set('person_id', filters.person_id);
    return p.toString();
  };

  return (
    <div className="p-6 space-y-4 overflow-auto h-full" data-testid="executive-gantt-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-[#09090B] flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Gantt PMO
          </h1>
          <p className="text-xs text-zinc-500">Plan, compromiso y carga sobre el mismo eje. Arrastrar simula: nada se escribe hasta confirmar.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      </div>

      <Card className="rounded-sm border-brand-border">
        <CardContent className="p-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
          <div>
            <label className="text-[10px] text-zinc-500">Capsula</label>
            <select className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5 bg-white"
              value={filters.capsule_id} onChange={(e) => setFilters((f) => ({ ...f, capsule_id: e.target.value, department: '' }))}>
              <option value="">Todas</option>
              {capsules.map((c) => <option key={c.capsule_id} value={c.capsule_id}>{c.name || c.capsule_id}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Gerencia</label>
            <select className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5 bg-white"
              value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}>
              <option value="">Todas</option>
              {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Prioridad</label>
            <select
              className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5 bg-white"
              value={filters.priority_in ? '' : filters.priority}
              onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value, priority_in: '' }))}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {filters.priority_in && !p ? `Multi (${filters.priority_in})` : (p || 'Todas')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Estado ticket</label>
            <select
              className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5 bg-white"
              value={filters.status_scope === 'open' ? '' : filters.status}
              onChange={(e) => setFilters((f) => ({
                ...f,
                status: e.target.value,
                status_scope: e.target.value ? '' : f.status_scope,
              }))}
            >
              <option value="">
                {filters.status_scope === 'open' ? 'Abiertos (scope)' : 'Todos'}
              </option>
              {ticketStatuses.map((s) => (
                <option key={s.id} value={s.id}>{s.name || s.id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Persona</label>
            {/* Era una caja de texto donde habia que **adivinar** el id: el filtro compara
                `assignee.id` exacto, asi que escribir el nombre en vez del correo devolvia un
                tablero vacio — que se lee como "esta persona no tiene nada" y no como
                "escribiste otra cosa". La lista la arma el backend con quien tiene trabajo
                **en este tablero**, antes de aplicar el propio filtro. */}
            <select
              className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5 bg-white"
              value={filters.person_id}
              onChange={(e) => setFilters((f) => ({ ...f, person_id: e.target.value }))}
              data-testid="gantt-filtro-persona"
            >
              <option value="">Todas</option>
              {personasDelTablero.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.name !== p.id ? ` · ${p.id}` : ''} ({p.tareas})
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <GanttToolbar
        meta={data?.meta}
        filters={filters}
        pxPorDia={pxPorDia}
        // **El zoom de la toolbar pasa por el grafico**, que es quien sabe donde esta el scroll:
        // llamar al setter crudo cambia la escala y deja el scroll donde estaba, asi que la fecha
        // que se estaba mirando se va de pantalla. La rueda con Ctrl ya anclaba; los botones no.
        onZoom={(v) => {
          const px = Math.max(PX_POR_DIA_MIN, Math.min(PX_POR_DIA_MAX, v));
          if (ganttApi.current?.zoomAnclandoElCentro) ganttApi.current.zoomAnclandoElCentro(px);
          else setPxPorDia(px);
        }}
        showAssignees={showAssignees}
        onShowAssigneesChange={setShowAssignees}
        verCarga={verCarga}
        onVerCargaChange={setVerCarga}
        guiaAbierta={guiaAbierta}
        onGuiaToggle={() => (guiaAbierta ? cerrarGuia() : setGuiaAbierta(true))}
        ventana={ventana}
        onVentanaChange={(v) => {
          setVentana(v);
          try { localStorage.setItem(VENTANA_ELEGIDA, v); } catch { /* preferencia perdida */ }
        }}
        onIrAHoy={() => ganttApi.current?.irAHoy()}
        fueraDeRango={fueraDeRango}
      />

      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
          ) : (
            <GanttChart
              apiRef={ganttApi}
              onFueraDeRango={setFueraDeRango}
              rows={data?.rows || []}
              dependencies={data?.dependencies || []}
              meta={data?.meta}
              carga={carga}
              verCarga={verCarga}
              pxPorDia={pxPorDia}
              onZoom={(v) => setPxPorDia(Math.max(PX_POR_DIA_MIN, Math.min(PX_POR_DIA_MAX, v)))}
              onRowClick={handleRowClick}
              showAssignees={showAssignees}
              propuesta={propuesta}
              puedeArrastrar={!sim && !simulando}
              onCambioDePlan={(cambio) => {
                const fila = filaDeTarea(cambio.task_id);
                simular([cambio], fila?.ticket_id, fila?.capsule_id, fila?.label);
              }}
              onNuevoVinculo={nuevoVinculo}
              onQuitarVinculo={quitarVinculo}
            />
          )}
        </div>
        {/* El diff le gana el lugar a la guia mientras esta pendiente: son dos cosas que se
            leen en el mismo momento y una de las dos es la que decide. */}
        {(sim || simulando || simError) ? (
          <GanttDiff
            diff={sim}
            titulo={simTitulo}
            simulando={simulando}
            aplicando={aplicando}
            error={simError}
            onConfirmar={confirmarSim}
            onDescartar={limpiarSim}
          />
        ) : (
          guiaAbierta && <GanttGuia rows={data?.rows || []} meta={data?.meta} carga={carga} onClose={cerrarGuia} />
        )}
      </div>

      {filters.person_id && (
        <p className="text-[10px] text-zinc-500">
          Filtrado por persona: <strong>{filters.person_id}</strong>.
          <Link className="text-brand-primary ml-1 underline" to={`/executive/gantt?${filterQuery().replace(/person_id=[^&]*&?/, '')}`}>
            Quitar filtro
          </Link>
        </p>
      )}

      <ConfirmActionDialog
        open={!!vinculoAQuitar}
        onOpenChange={(abierto) => { if (!abierto) setVinculoAQuitar(null); }}
        title="¿Quitar esta dependencia?"
        description={`El plan de la tarea que esperaba deja de estar atado. ${vinculoAQuitar?.titulo || ''}`}
        confirmText="Quitar"
        // No va en rojo: el vínculo se vuelve a crear con dos toques, y un rojo que aparece para
        // todo entrena a ignorarlo el día que está sobre algo irreversible.
        destructivo={false}
        onConfirm={confirmarQuitarVinculo}
      />
    </div>
  );
}
